import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";

import Button from "features/components/Button";
import {
  FILTER_INDENT_GROUP_MARGIN_PX,
  FILTER_INDENT_MARGIN_PX,
  FILTER_ROW_OPACITY_PERCENTAGE_INCREASE,
} from "features/ui/Filters/FilterBuilder/constants";
import AnyAllSelect, {
  AnyAll,
} from "features/ui/Filters/FilterBuilder/FilterGroup/AnyAllSelect";
import FilterRow from "features/ui/Filters/FilterBuilder/FilterRow";
import RowActions from "features/ui/Filters/FilterBuilder/FilterRow/RowActions";
import { OnRowDataChangeHandle } from "features/ui/Filters/FilterBuilder/FilterRow/types";
import {
  FilterGroupState,
  FilterRowState,
} from "features/ui/Filters/FilterBuilder/types";
import { SchemaEntry } from "features/ui/MaterialTable";

interface Props {
  schema: SchemaEntry<string>[];
  id: string;
  depth: number;
  children: (FilterRowState | FilterGroupState)[];
  onNewRow: (id: string) => void;
  onNewGroup: (id: string) => void;
  onDelete?: (id: string) => void;
  anyAll: AnyAll;
  onGroupOperatorChange: (id: string, anyAll: AnyAll) => void;
  onRowDataChange: OnRowDataChangeHandle;
  onResetToDefault?: () => void;
  onClear?: () => void;
  disabled?: boolean;
  disableDelete?: boolean;
  attributePlaceholder?: string;
  embedded?: boolean;
  hasChanges?: boolean;
  hasInitialFocus?: boolean;
  occursFilterSelector?: boolean;
  hasTopLevelActions?: boolean;
  disableGroups?: boolean;
}

const FilterGroup = ({
  schema,
  id,
  depth,
  children,
  onNewRow,
  onNewGroup,
  onDelete,
  disableDelete,
  anyAll,
  onGroupOperatorChange,
  onRowDataChange,
  onResetToDefault,
  onClear,
  disabled,
  attributePlaceholder,
  embedded,
  hasChanges,
  hasInitialFocus,
  occursFilterSelector,
  hasTopLevelActions,
  disableGroups,
}: Props) => {
  const { t } = useTranslation("filters");
  const [hasInputFocus, setHasInputFocus] = useState(false);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleFocus = () => {
    // Clear any pending blur timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setHasInputFocus(true);
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Only set a timeout to blur if we're not focusing another element within the same row
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      timeoutRef.current = setTimeout(() => {
        setHasInputFocus(false);
      }, 50); // Small delay to allow for the next focus to occur
    }
  };

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  const shouldDisplayResetToDefaultButton =
    depth === 0 && onResetToDefault && !embedded && hasTopLevelActions;

  return (
    <div
      style={
        depth === 0
          ? undefined
          : {
              marginLeft: `${FILTER_INDENT_GROUP_MARGIN_PX}px`,
              paddingLeft: `${
                FILTER_INDENT_MARGIN_PX - FILTER_INDENT_GROUP_MARGIN_PX
              }px`,
              background: `rgba(0,0,0, ${depth * FILTER_ROW_OPACITY_PERCENTAGE_INCREASE}%)`,
            }
      }
      className={classNames("rounded-sm", {
        "mb-1 pb-1": !embedded,
        "mt-1 pt-1 pb-1": depth > 0,
      })}
      data-testid="filter-group"
      onFocusCapture={handleFocus}
      onBlurCapture={handleBlur}
      id={id}
    >
      <div
        className="flex items-center justify-between group/group w-auto"
        data-testid="group-actions"
      >
        {!embedded && !disableGroups && (
          <AnyAllSelect
            selected={anyAll}
            onChange={(selected) => onGroupOperatorChange(id, selected.id)}
            disabled={disabled}
            autoFocus={false}
          />
        )}
        {shouldDisplayResetToDefaultButton && (
          <div className="flex flex-row items-center gap-1">
            <Button
              color="secondary"
              size="small"
              className="text-nowrap"
              onClick={onClear}
              data-testid="clear-all"
              disabled={disabled}
            >
              <span className="text-xs">{t("actions.clear")}</span>
            </Button>
            <Button
              color="secondary"
              size="small"
              className="text-nowrap"
              onClick={onResetToDefault}
              data-testid="reset-all-to-default"
              disabled={disabled}
            >
              <span className="text-xs">{t("actions.restore_defaults")}</span>
            </Button>
          </div>
        )}
        {depth > 0 && (
          <RowActions
            id={id}
            onNewRow={onNewRow}
            onNewGroup={onNewGroup}
            onDelete={disableDelete ? undefined : onDelete}
            disabled={disabled}
            hideAddGroupCta={embedded || disableGroups}
            hasInputFocus={hasInputFocus}
            isGroup={true}
          />
        )}
      </div>
      {/* Each group can contain any combination of rows or groups */}
      {children.map((child) => {
        if (child.type === "row") {
          return (
            <FilterRow
              key={child.id}
              onNewRow={onNewRow}
              onNewGroup={onNewGroup}
              onDelete={disableDelete ? undefined : onDelete}
              onRowDataChange={onRowDataChange}
              schema={schema}
              disabled={disabled}
              attributePlaceholder={attributePlaceholder}
              embedded={embedded}
              depth={depth}
              occursFilterSelector={occursFilterSelector}
              hasInitialFocus={hasInitialFocus}
              disableGroups={disableGroups}
              {...child}
            />
          );
        } else {
          return (
            <FilterGroup
              schema={schema}
              key={child.id}
              id={child.id}
              depth={depth + 1}
              children={child.children}
              onNewRow={onNewRow}
              onNewGroup={onNewGroup}
              onDelete={onDelete}
              disableDelete={disableDelete}
              anyAll={child.anyAll}
              onClear={onClear}
              onGroupOperatorChange={onGroupOperatorChange}
              onRowDataChange={onRowDataChange}
              disabled={disabled}
              attributePlaceholder={attributePlaceholder}
              hasInitialFocus={hasInitialFocus}
              hasChanges={hasChanges}
              disableGroups={disableGroups}
            />
          );
        }
      })}
      <div className="flex flex-row gap-2 ml-4">
        <Button
          size="small"
          variant="text"
          startIcon={<AddOutlinedIcon />}
          disabled={disabled}
          data-testid="filter-action-add-row"
          onClick={() =>
            onNewRow(
              children.length > 0 ? children[children.length - 1].id : id
            )
          }
        >
          {t("actions.add_comparison")}
        </Button>

        {!disableGroups && (
          <Button
            size="small"
            variant="text"
            startIcon={<AddBoxOutlinedIcon />}
            disabled={disabled}
            data-testid="filter-action-add-group"
            className="p-1"
            onClick={() =>
              onNewGroup(
                children.length > 0 ? children[children.length - 1].id : id
              )
            }
          >
            {t("actions.add_group")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default FilterGroup;
